import { Link } from 'react-router';

/**
 * CAB-505: the Privacy Policy body, extracted from PrivacyPage so the same
 * text can render both on the public /legal/privacy page and inside the
 * LegalConsentModal. Kept in shared/ to respect feature-isolation.
 */
export function PrivacyContent() {
  return (
    <article className="space-y-8 text-sm leading-relaxed">
      <header className="space-y-1">
        <p className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
          General Technologies Inc.
        </p>
        <h1 className="text-foreground text-3xl font-bold">Privacy Policy</h1>
        <p className="text-muted-foreground text-xs italic">Last updated: January 8, 2026</p>
      </header>

      <section className="space-y-4">
        <h2 className="text-foreground text-base font-semibold tracking-wide uppercase">
          Overview
        </h2>
        <p>
          This Privacy Policy (the &quot;Privacy Policy&quot;) explains how GENERAL TECHNOLOGIES
          (&quot;we&quot;, &quot;us&quot; or &quot;our&quot;) collects, uses, discloses, and retains
          information (the &quot;Information&quot;) that you (&quot;you&quot;, &quot;your&quot;)
          provide to us or which we otherwise collect when you access and use our website, the
          ChartAI app, and other services (collectively, the &quot;Services&quot;).
        </p>
        <p>
          The terms of this Privacy Policy are incorporated into and form part of our{' '}
          <Link to="/legal/terms" className="text-primary hover:text-primary/80 underline">
            Terms of Service
          </Link>
          . To the extent of any conflict between this Privacy Policy and the Terms of Service, the
          Terms of Service will apply; however, if the parties have entered into a{' '}
          <strong>Business Associate Agreement (BAA)</strong> and/or{' '}
          <strong>Data Processing Agreement (DPA)</strong>, the BAA/DPA will govern to the extent of
          any conflict relating to PHI, special category data, security safeguards, breach/incident
          notification, retention, or permitted uses and disclosures.
        </p>
        <div className="bg-muted rounded-lg p-4 text-xs font-semibold uppercase">
          BY ACCESSING AND USING THE SERVICES, YOU AGREE TO BE BOUND BY THIS PRIVACY POLICY. IF YOU
          DO NOT AGREE, PLEASE DO NOT ACCESS AND USE THE SERVICES.
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="text-foreground text-base font-semibold tracking-wide uppercase">
          Contacting Us
        </h2>
        <p>If you have questions or complaints about how we handle your information, or wish to:</p>
        <ul className="text-muted-foreground list-disc space-y-1 pl-6">
          <li>ask questions,</li>
          <li>update or delete your account,</li>
          <li>change user preferences,</li>
          <li>register a concern,</li>
          <li>opt out of marketing, or</li>
          <li>exercise any of your data rights,</li>
        </ul>
        <p>
          You may contact us at{' '}
          <a
            href="mailto:privacy@generaltech.io"
            className="text-primary hover:text-primary/80 font-semibold"
          >
            privacy@generaltech.io
          </a>
          . We will respond to all inquiries within 30 days. You may be asked to verify your
          identity.
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-foreground text-base font-semibold tracking-wide uppercase">
          About Us
        </h2>
        <p>
          We provide the ChartAI Platform to licensed dental professionals to assist with ambient
          clinical documentation and related services. The Services support charting, patient
          communication, AI-powered automation, and enhanced clinical workflows.
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-foreground text-base font-semibold tracking-wide uppercase">
          Definitions
        </h2>
        <ul className="text-muted-foreground list-disc space-y-2 pl-6">
          <li>
            <strong className="text-foreground">&quot;you&quot;</strong> refers to the user of our
            Platform and Services.
          </li>
          <li>
            <strong className="text-foreground">&quot;your information&quot;</strong> includes your
            personal, professional, or health information.
          </li>
          <li>
            <strong className="text-foreground">&quot;privacy laws&quot;</strong> includes PIPEDA
            and PIPA (British Columbia) applicable health information laws, and any other
            jurisdictional laws that apply to us.
          </li>
        </ul>
      </section>

      <section className="space-y-3">
        <h2 className="text-foreground text-base font-semibold tracking-wide uppercase">
          Types of Information We Collect
        </h2>
        <p>We collect the following types of data:</p>
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted">
              <tr>
                <th className="text-foreground px-4 py-3 text-left font-semibold">Category</th>
                <th className="text-foreground px-4 py-3 text-left font-semibold">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              <tr>
                <td className="px-4 py-3 font-medium">General Personal Information</td>
                <td className="text-muted-foreground px-4 py-3">
                  Name, contact details, age/date of birth, gender, education, professional
                  qualifications, etc.
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 font-medium">Payment and Claim Information</td>
                <td className="text-muted-foreground px-4 py-3">
                  Credit card details, bank account, and insurance/claims data.
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 font-medium">Sensitive Health Information</td>
                <td className="text-muted-foreground px-4 py-3">
                  Clinical notes, health history, test results, diagnoses, medications.
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 font-medium">Device and Log Information</td>
                <td className="text-muted-foreground px-4 py-3">
                  Device ID, type, IP address, geolocation, usage stats, cookies.
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 font-medium">Customer Feedback or Surveys</td>
                <td className="text-muted-foreground px-4 py-3">
                  Information submitted voluntarily for product or service improvement.
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 font-medium">De-Identified Data</td>
                <td className="text-muted-foreground px-4 py-3">
                  Used for analytics, performance improvements and AI training purposes as set out
                  in this agreement.
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 font-medium">Recruitment Data</td>
                <td className="text-muted-foreground px-4 py-3">
                  If you apply for a job with us, we collect standard employment and reference data.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="text-foreground text-base font-semibold tracking-wide uppercase">
          Patient Information
        </h2>
        <p>Our application only collects personal information that is either:</p>
        <ul className="text-muted-foreground list-disc space-y-1 pl-6">
          <li>directly entered by authorised healthcare practitioners, or</li>
          <li>captured during their use of the application.</li>
        </ul>
        <p>
          The collection of this data, including device metadata, is essential for improving the
          user experience, optimising performance, and enforcing strong security protocols. For
          patient/clinical information processed through the Services, the healthcare
          practitioner/clinic generally acts as the data controller (or equivalent under applicable
          law), and General Technologies acts as a processor/service provider processing such
          information on the controller&apos;s documented instructions.
        </p>
        <p>
          Where GDPR applies, processing of data concerning health requires an applicable{' '}
          <strong>Article 9</strong> condition in addition to an Article 6 lawful basis. Where
          Canadian private-sector laws apply, the clinic/practitioner is responsible for obtaining
          any required patient consent and providing appropriate notices for collection, use, and
          disclosure of sensitive information.
        </p>
        <p>
          As part of its core functionality, the application can generate summaries of clinical
          discussions. This means the entire conversation between a healthcare provider and their
          patient may be recorded, including sensitive personal health information. Audio recordings
          are used to generate a text-based transcript (the &quot;Transcript&quot;) and to support
          additional clinical workflows that aid documentation and care delivery.
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-foreground text-base font-semibold tracking-wide uppercase">
          Transcripts
        </h2>
        <p>
          General Technologies uses advanced machine learning and artificial intelligence
          technologies to analyse Transcripts in order to deliver core functionality and
          continuously improve our services. Processing these Transcripts is essential: without it,
          the application cannot function as intended.
        </p>
        <p className="font-medium">
          We do not disclose Transcripts or audio recordings to third parties except (a) to
          contracted service providers operating the Services on our behalf under confidentiality
          and security obligations and only to the minimum extent necessary, (b) where you authorize
          disclosure (including via integrations), or (c) as required by law.
        </p>
        <p>
          We rely on healthcare practitioners to obtain any necessary patient consent prior to using
          the application. If a practitioner does not have the patient&apos;s consent to collect and
          use their personal information in this way, the application must not be used with that
          patient.
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-foreground text-base font-semibold tracking-wide uppercase">
          Voice Data
        </h2>
        <p>
          By accessing or using the Voice Service, you agree to the collection and processing of
          voice data. This &quot;Voice Data&quot; may include information such as audio recordings
          from the microphone during your use of the Service. When you use our voice services, we
          collect and use your voice input and other information to respond to your requests,
          provide the requested Service to you, and improve our services.
        </p>
        <p>
          We obtain your express consent to collect and process voice data and reaffirm consent
          periodically, especially when dealing with sensitive information. The collection and
          processing of voice data comply with all applicable privacy laws in British Columbia,
          including requirements for consent, purpose limitation, and data security.
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-foreground text-base font-semibold tracking-wide uppercase">
          Log Data
        </h2>
        <p>
          We may also collect information that your browser or mobile device sends whenever you
          visit our Service (&quot;Log Data&quot;). This Log Data may include information such as
          your computer&apos;s Internet Protocol (&quot;IP&quot;) address, browser type, browser
          version, the pages of our Service that you visit, the time and date of your visit, the
          time spent on those pages, unique device identifiers, and other statistics.
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-foreground text-base font-semibold tracking-wide uppercase">
          How We Collect Information
        </h2>
        <p>We collect information:</p>
        <ul className="text-muted-foreground list-disc space-y-1 pl-6">
          <li>directly from you during registration or usage of the Platform,</li>
          <li>through communications (email, chat, calls),</li>
          <li>through cookies and analytics,</li>
          <li>from your employer (for employees),</li>
          <li>from public and third-party sources (for verification or recruitment),</li>
          <li>via authorized integrations or services.</li>
        </ul>
      </section>

      <section className="space-y-3">
        <h2 className="text-foreground text-base font-semibold tracking-wide uppercase">
          How We Use This Information
        </h2>
        <p>We use your information for:</p>
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted">
              <tr>
                <th className="text-foreground px-4 py-3 text-left font-semibold">Purpose</th>
                <th className="text-foreground px-4 py-3 text-left font-semibold">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              <tr>
                <td className="px-4 py-3 font-medium">Access</td>
                <td className="text-muted-foreground px-4 py-3">Enable use of our Services.</td>
              </tr>
              <tr>
                <td className="px-4 py-3 font-medium">Service Performance</td>
                <td className="text-muted-foreground px-4 py-3">
                  Improve user experience, AI features, analytics.
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 font-medium">Healthcare Services</td>
                <td className="text-muted-foreground px-4 py-3">
                  Facilitate clinical services through the ChartAI Platform.
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 font-medium">Support</td>
                <td className="text-muted-foreground px-4 py-3">
                  Notify you of updates, bugs, changes, and respond to queries.
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 font-medium">Marketing</td>
                <td className="text-muted-foreground px-4 py-3">
                  Share offers and updates only if consented. No health info used for marketing.
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 font-medium">Legal Compliance</td>
                <td className="text-muted-foreground px-4 py-3">
                  Respond to requests from regulators or law enforcement.
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 font-medium">Employment</td>
                <td className="text-muted-foreground px-4 py-3">Evaluate job applications.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="text-foreground text-base font-semibold tracking-wide uppercase">
          AI Training and Development
        </h2>
        <p>
          We may use <strong>de-identified or pseudonymized information</strong>, including clinical
          and operational data, to improve and train our artificial intelligence (AI) models,{' '}
          <strong>but only where permitted by law and with the necessary consent</strong>.
        </p>
        <p>When we process personal information for the purpose of AI model development:</p>
        <ul className="text-muted-foreground list-disc space-y-1 pl-6">
          <li>
            We apply{' '}
            <strong className="text-foreground">
              data minimization and pseudonymization techniques
            </strong>{' '}
            to reduce re-identification risk.
          </li>
          <li>
            We <strong className="text-foreground">do not use identifiable patient data</strong> for
            model training unless we have obtained{' '}
            <strong className="text-foreground">explicit informed consent</strong> from the
            individual or the healthcare provider authorised to provide it on their behalf.
          </li>
          <li>
            Where applicable, we ensure training datasets are processed in accordance with{' '}
            <strong className="text-foreground">provincial and federal privacy legislation</strong>,
            including PIPEDA, PIPA (BC), and Quebec&apos;s Private Sector Act.
          </li>
          <li>
            We conduct <strong className="text-foreground">privacy impact assessments</strong> and
            employ <strong className="text-foreground">technical safeguards</strong> to prevent
            inference or membership attacks and to ensure data is not regurgitated in outputs.
          </li>
        </ul>
        <p>
          If personal information is used in a manner that could reasonably be expected to impact
          individual privacy, we will provide{' '}
          <strong>clear notice, justification, and opt-out mechanisms</strong> where possible.
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-foreground text-base font-semibold tracking-wide uppercase">
          Marketing and Opt-Out
        </h2>
        <p>We may send emails or promotional materials related to our services. You can:</p>
        <ul className="text-muted-foreground list-disc space-y-1 pl-6">
          <li>unsubscribe via any message you receive, or</li>
          <li>
            email{' '}
            <a
              href="mailto:privacy@generaltech.io"
              className="text-primary hover:text-primary/80 font-semibold"
            >
              privacy@generaltech.io
            </a>
            .
          </li>
        </ul>
        <p>
          We do <strong>not</strong> use health information for marketing. We do{' '}
          <strong>not</strong> share information with third parties for their own marketing purposes
          without your explicit consent.
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-foreground text-base font-semibold tracking-wide uppercase">
          Data Storage
        </h2>
        <p className="font-semibold">We store personal information of Canadian users in Canada.</p>
        <p>
          We recognise the importance of data sovereignty and are committed to safeguarding personal
          information. Accordingly, personal information of users located in Canada is stored on
          servers within Canada.
        </p>
        <p>
          Where Québec law applies, before communicating personal information outside Québec, we
          conduct a privacy impact assessment and implement appropriate contractual and technical
          safeguards to ensure the information would receive protection equivalent to that provided
          under Québec law.
        </p>
        <p>
          For users located outside of Canada, we store and process personal information in
          jurisdictions appropriate to the delivery of our Services and in accordance with the legal
          requirements of the applicable region.
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-foreground text-base font-semibold tracking-wide uppercase">
          Information Sharing
        </h2>
        <p>We may share data with:</p>
        <ul className="text-muted-foreground list-disc space-y-1 pl-6">
          <li>our staff and affiliates,</li>
          <li>third-party service providers under contract,</li>
          <li>regulators and legal authorities,</li>
          <li>parties to a corporate transaction,</li>
          <li>anyone you authorize us to share with (e.g., via integrations).</li>
        </ul>
        <p>
          We ensure third parties follow the same or stronger privacy practices via contractual
          terms.
        </p>
        <h3 className="text-foreground font-semibold">
          How and why we share your information with trusted third parties
        </h3>
        <p>
          To operate our website and application effectively, we engage trusted third-party
          providers to support essential functions such as:
        </p>
        <ul className="text-muted-foreground list-disc space-y-1 pl-6">
          <li>Fraud detection and prevention</li>
          <li>Payment processing and transaction notifications</li>
          <li>Delivering requested services or information</li>
          <li>Maintaining and improving the functionality of our website and application</li>
          <li>Providing customer support</li>
        </ul>
        <p>
          In the event of a corporate transaction such as a merger, acquisition, reorganisation, or
          sale of our business, we may transfer personal information as part of that process. In
          such cases, we will ensure that any transferred data remains protected in a manner
          consistent with this Privacy Policy.
        </p>
        <p>
          Except as described above,{' '}
          <strong>
            General Technologies will not use or share any personal information collected from users
            or patients without proper authorisation.
          </strong>
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-foreground text-base font-semibold tracking-wide uppercase">
          Cookies and Analytics
        </h2>
        <p>
          Cookies are files with a small amount of data, which may include an anonymous unique
          identifier. Cookies are sent to your browser from a website and stored on your
          computer&apos;s hard drive. We use &quot;cookies&quot; to collect information. You can
          instruct your browser to refuse all cookies or to indicate when a cookie is being sent.
          However, if you do not accept cookies, you may be unable to use some portions of our
          Service.
        </p>
        <p>
          Third-party analytics tools (e.g., Google Analytics) do <strong>not</strong> receive any
          Protected Health Information (PHI) or patient data.
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-foreground text-base font-semibold tracking-wide uppercase">
          How We Safeguard Your Information
        </h2>
        <p>We employ:</p>
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted">
              <tr>
                <th className="text-foreground px-4 py-3 text-left font-semibold">Measure</th>
                <th className="text-foreground px-4 py-3 text-left font-semibold">Description</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              <tr>
                <td className="px-4 py-3 font-medium">Encryption</td>
                <td className="text-muted-foreground px-4 py-3">
                  Bank-grade encryption at rest and in transit.
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 font-medium">Access Controls</td>
                <td className="text-muted-foreground px-4 py-3">
                  Role-based permissions for all staff and vendors.
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 font-medium">Staff Training</td>
                <td className="text-muted-foreground px-4 py-3">
                  Continuous privacy and security training.
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 font-medium">De-identification</td>
                <td className="text-muted-foreground px-4 py-3">
                  Sensitive data is pseudonymized or aggregated where appropriate.
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 font-medium">Retention</td>
                <td className="text-muted-foreground px-4 py-3">
                  We retain data only as long as necessary or legally required.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="text-foreground text-base font-semibold tracking-wide uppercase">
          Confidentiality Incidents / Privacy Breaches
        </h2>
        <p>
          We maintain a record (register) of confidentiality incidents/privacy breaches as required
          by applicable law. Where a breach of security safeguards involving personal information
          creates a real risk of significant harm, we will notify affected individuals and report to
          the Office of the Privacy Commissioner of Canada as required under PIPEDA.
        </p>
        <p>
          Where Québec&apos;s private-sector law (as amended by Law 25) applies, if a
          confidentiality incident presents a risk of serious injury, we will notify the Commission
          d&apos;accès à l&apos;information du Québec (CAI) and affected individuals as required.
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-foreground text-base font-semibold tracking-wide uppercase">
          Your Rights
        </h2>
        <p>You have the right to:</p>
        <ul className="text-muted-foreground list-disc space-y-1 pl-6">
          <li>access your information,</li>
          <li>correct inaccurate data,</li>
          <li>request deletion or anonymization,</li>
          <li>request portability in a machine-readable format,</li>
          <li>object or withdraw consent (subject to legal exceptions),</li>
          <li>lodge a complaint with the appropriate regulator.</li>
        </ul>
        <p>
          Please send your requests to{' '}
          <a
            href="mailto:privacy@generaltech.io"
            className="text-primary hover:text-primary/80 font-semibold"
          >
            privacy@generaltech.io
          </a>
          .
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-foreground text-base font-semibold tracking-wide uppercase">
          Employees
        </h2>
        <p>We collect the following from employees or applicants:</p>
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted">
              <tr>
                <th className="text-foreground px-4 py-3 text-left font-semibold">Category</th>
                <th className="text-foreground px-4 py-3 text-left font-semibold">Example</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              <tr>
                <td className="px-4 py-3 font-medium">Personal</td>
                <td className="text-muted-foreground px-4 py-3">Name, contact, image</td>
              </tr>
              <tr>
                <td className="px-4 py-3 font-medium">Educational</td>
                <td className="text-muted-foreground px-4 py-3">
                  Institutions, certifications, references
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 font-medium">Sensitive</td>
                <td className="text-muted-foreground px-4 py-3">
                  Health conditions, diversity data (where required or permitted)
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 font-medium">Financial</td>
                <td className="text-muted-foreground px-4 py-3">
                  Bank info, tax ID, compensation history
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 font-medium">Work-Related</td>
                <td className="text-muted-foreground px-4 py-3">
                  Work history, professional memberships, performance data
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="text-foreground text-base font-semibold tracking-wide uppercase">
          Children&apos;s Privacy
        </h2>
        <p>
          Our Service does not address anyone under the age of 13 (&quot;Children&quot;). We do not
          knowingly collect personally identifiable information from children under 13. If you are a
          parent or guardian and you are aware that your Children have provided us with Personal
          Information, please contact us. If we become aware that we have collected Personal
          Information from children under age 13 without verification of parental consent, we take
          steps to remove that information from our servers.
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-foreground text-base font-semibold tracking-wide uppercase">
          Third Party Sites
        </h2>
        <p>
          Our Services may contain links to external websites. We are not responsible for the
          privacy practices of those sites. Review their policies independently.
        </p>
        <h3 className="text-foreground font-semibold">Links to Other Sites</h3>
        <p>
          Our Service may contain links to other sites not operated by us. If you click on a
          third-party link, you will be directed to that third-party&apos;s site. We strongly advise
          you to review the Privacy Policy of every site you visit. We have no control over and
          assume no responsibility for the content, privacy policies, or practices of any
          third-party sites or services.
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-foreground text-base font-semibold tracking-wide uppercase">
          Privacy Officer
        </h2>
        <p>
          We have appointed a Privacy Officer who is responsible for overseeing our compliance with
          applicable privacy laws, including the Personal Information Protection Act (PIPA) of
          British Columbia. If you have any questions, concerns, or requests regarding your Personal
          Information or our privacy practices, please contact our Privacy Officer at{' '}
          <a href="mailto:privacy@generaltech.io" className="text-primary hover:text-primary/80">
            privacy@generaltech.io
          </a>
          .
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-foreground text-base font-semibold tracking-wide uppercase">General</h2>
        <p>
          We may modify this Privacy Policy at any time, so please review it periodically. By
          continuing to use the Services after any modification, you consent to be bound by the
          modified Privacy Policy. This Privacy Policy is governed by the laws of British Columbia.
        </p>
        <p>
          If you have any questions about this Privacy Policy, please contact us at:{' '}
          <a
            href="mailto:privacy@generaltech.io"
            className="text-primary hover:text-primary/80 font-semibold"
          >
            privacy@generaltech.io
          </a>
          .
        </p>
      </section>
    </article>
  );
}
